import {
  avatar,
  background,
  captions,
  card,
  speaker,
  text,
  visualizer,
} from './helpers'
import type { TemplateBlueprint } from './types'

/**
 * `PostcardLayout` (`layouts/PostcardLayout.tsx`): a centred card — two
 * subtitle lines over two italic title lines, the speaker's avatar in a
 * mirrored row of bars, their handle, captions at the foot. The bars and
 * avatar sit where v1 put them under a 2+2-line header. Intended
 * differences: a shorter header does not pull the bars up (no flex flow).
 * Intended difference (#547): the square captions' box is 235 px tall, not
 * 321, so their foot sits 86 px higher, clear of the watermark.
 */
const bars = { variant: 'mirrored' as const }
const handle = { weight: 400, lineHeight: 1.5 }

export const postcardBlueprint: TemplateBlueprint = {
  kind: 'audio',
  orientations: {
    portrait: [
      background(),
      card('portrait', 'card', [0, 160, 1080, 1600]),
      text(
        'portrait',
        'subtitle',
        [103, 260, 874, 106],
        { size: 40, weight: 400, lineHeight: 1.32 },
        { align: 'center', surface: 'border' },
      ),
      text(
        'portrait',
        'title',
        [103, 382, 874, 158],
        { size: 60, weight: 400, lineHeight: 1.32 },
        { align: 'center', fontStyle: 'italic', surface: 'border' },
      ),
      visualizer('portrait', [103, 704, 874, 308], bars, { surface: 'border' }),
      avatar('portrait', 386, 704, 308),
      {
        ...speaker(
          'portrait',
          [103, 1073, 874, 81],
          'handle',
          { size: 54, ...handle },
          { align: 'center', surface: 'border' },
        ),
        id: 'speaker',
      },
      captions('portrait', [103, 1240, 874, 420], 'end', { surface: 'border' }),
    ],
    square: [
      background(),
      card('square', 'card', [140, 0, 800, 1080]),
      text(
        'square',
        'subtitle',
        [216, 39, 648, 85],
        { size: 32, weight: 400, lineHeight: 1.32 },
        { align: 'center', surface: 'border' },
      ),
      text(
        'square',
        'title',
        [216, 139, 648, 137],
        { size: 52, weight: 400, lineHeight: 1.32 },
        { align: 'center', fontStyle: 'italic', surface: 'border' },
      ),
      visualizer('square', [216, 400, 648, 216], bars, { surface: 'border' }),
      avatar('square', 432, 400, 216),
      {
        ...speaker(
          'square',
          [216, 653, 648, 54],
          'handle',
          { size: 36, ...handle },
          { align: 'center', surface: 'border' },
        ),
        id: 'speaker',
      },
      captions('square', [216, 720, 648, 235], 'end', { surface: 'border' }),
    ],
    landscape: [
      background(),
      card('landscape', 'card', [175, 0, 1570, 1080]),
      text(
        'landscape',
        'subtitle',
        [275, 70, 1370, 53],
        { size: 40, weight: 400, lineHeight: 1.32 },
        { align: 'center', surface: 'border' },
      ),
      text(
        'landscape',
        'title',
        [275, 139, 1370, 158],
        { size: 60, weight: 400, lineHeight: 1.32 },
        { align: 'center', fontStyle: 'italic', surface: 'border' },
      ),
      visualizer('landscape', [510, 380, 900, 220], bars, {
        surface: 'border',
      }),
      avatar('landscape', 850, 380, 220),
      {
        ...speaker(
          'landscape',
          [510, 649, 900, 66],
          'handle',
          { size: 44, ...handle },
          { align: 'center', surface: 'border' },
        ),
        id: 'speaker',
      },
      captions('landscape', [275, 710, 1370, 300], 'end', {
        surface: 'border',
      }),
    ],
  },
}
